'use client';

import { ReactComponent as ChevronIcon } from '@assets/icons/chevron-right.svg';
import { ReactComponent as ReceiptIcon } from '@assets/icons/receipt.svg';
import { outputSameElseDateFormat, ROUTES } from 'constant';
import Link from 'next/link';

import {
  Box,
  Flex,
  Grid,
  GridItem,
  IconButton,
  Text,
  useBreakpointValue,
  useTheme
} from '@chakra-ui/react';

import { formatCurrency, getCustomFormattedDay } from 'utils';

import { PackageInterface } from 'types';

import { Button, Divider } from 'ui';

interface PackageItemProps {
  item: PackageInterface;
  isLastItem: boolean;
  onBuyHandler: () => void;
}

const PackageItem = ({ item, isLastItem, onBuyHandler }: PackageItemProps) => {
  const {
    colors: {
      palette: {
        dashboard: {
          packages: {
            packagesList: { packageItem }
          }
        }
      }
    }
  } = useTheme();

  const isTablet = useBreakpointValue(
    {
      base: true,
      lg: false
    },
    { fallback: 'lg' }
  );

  return (
    <Box overflowX='hidden'>
      <Grid
        templateColumns={{
          base: 'repeat(7, 1fr)',
          lg: 'repeat(24, 1fr)'
        }}
        templateRows={{
          base: 'repeat(4, auto)',
          lg: 'repeat(1, 1fr)'
        }}
        px={{ base: '15px', md: '20px' }}
        py={{ base: '21px', lg: '17px' }}
        columnGap='20px'
        rowGap='15px'
      >
        <GridItem colSpan={{ base: 5, lg: 8 }}>
          <Flex h='100%' alignItems='center' justifyContent='start'>
            <Text
              as={Link}
              href={`${ROUTES.packages}/${item.id}`}
              fontSize='18px'
              fontWeight={{ base: 'semibold', lg: 'normal' }}
              color={packageItem.nameTextColor}
            >
              {item.name}
            </Text>
          </Flex>
        </GridItem>
        <GridItem colSpan={{ base: 2, lg: 3 }}>
          <Flex
            h='100%'
            alignItems='center'
            justifyContent='center'
            position='relative'
            px='15px'
          >
            <Text fontSize='16px' color={packageItem.unitsTextColor}>
              {item.units}
            </Text>
            <IconButton
              as={Link}
              href={`${ROUTES.packages}/${item.id}`}
              position='absolute'
              right='-20px'
              display={{ base: 'flex', lg: 'none' }}
              justifyContent='center'
              alignItems='center'
              bg='transparent'
              aria-label='view package'
              icon={
                <ChevronIcon
                  fill={packageItem.iconColor}
                  width={8}
                  height={16}
                />
              }
            />
          </Flex>
        </GridItem>

        <GridItem
          display={{ base: 'block', lg: 'none' }}
          colSpan={7}
          gridRowStart={2}
        >
          <Divider
            position='absolute'
            width='calc(100% - 40px)'
            left='20px'
            borderBottomColor={packageItem.dividerColor}
          />
        </GridItem>

        <GridItem colSpan={4}>
          <Flex
            height='100%'
            justifyContent='start'
            gap='10px'
            flexDirection={{
              base: 'column',
              lg: 'row'
            }}
            alignItems={{
              base: 'start',
              lg: 'center'
            }}
            paddingLeft={{ base: 0, lg: '25%' }}
          >
            <Text
              display={{ base: 'block', lg: 'none' }}
              fontSize='12px'
              color={packageItem.expiresLabelColor}
              textTransform='uppercase'
            >
              Expires
            </Text>
            <Text fontSize='16px' color={packageItem.expiresTextColor}>
              {item.exp_date
                ? getCustomFormattedDay(item.exp_date, outputSameElseDateFormat)
                : 'Never'}
            </Text>
          </Flex>
        </GridItem>
        <GridItem colSpan={4}>
          <Flex
            height='100%'
            justifyContent='end'
            gap='10px'
            alignItems={{
              base: 'start',
              lg: 'center'
            }}
            flexDirection={{
              base: 'column',
              lg: 'row'
            }}
          >
            <Text
              display={{ base: 'block', lg: 'none' }}
              fontSize='12px'
              color={packageItem.amountLabelColor}
              textTransform='uppercase'
            >
              Amount
            </Text>
            <Text
              fontSize={{ base: '16px', md: '18px', lg: '21px' }}
              color={packageItem.amountTextColor}
              textTransform='uppercase'
            >
              {formatCurrency(item.price)}
            </Text>
          </Flex>
        </GridItem>

        <GridItem
          gridColumnStart={{ base: 5, lg: 20 }}
          colSpan={{ base: 3, lg: 2 }}
          gridRowStart={{ base: 3, lg: 1 }}
          rowSpan={{ base: 2, lg: 'auto' }}
        >
          {!!item.allow_ordering && (
            <Flex
              h='100%'
              justifyContent={{ base: 'end', lg: 'end' }}
              alignItems='center'
            >
              <Button
                colorScheme={isTablet ? 'secondary' : 'main'}
                variant={{ base: 'outline', lg: 'link' }}
                width={{ base: '90px', lg: 'auto' }}
                height={{ base: '32px', lg: 'auto' }}
                color={packageItem.buttonTextColor}
                fontSize='12px'
                justifyContent='center'
                onClick={onBuyHandler}
                _hover={{
                  textDecoration: 'none'
                }}
              >
                buy
              </Button>
            </Flex>
          )}
        </GridItem>

        <GridItem colSpan={3} display={{ base: 'none', lg: 'block' }}>
          <Flex h='100%' alignItems='center' justifyContent='space-between'>
            <IconButton
              aria-label='receipt'
              bg='transparent'
              as={Link}
              href={item?.receipt_url || '/'}
              target='_blank'
              icon={
                <ReceiptIcon
                  fill={packageItem.receiptIconColor}
                  width={20}
                  height={23}
                />
              }
              visibility={item?.receipt_url ? 'visible' : 'hidden'}
            />
            <IconButton
              aria-label='receipt'
              as={Link}
              href={`${ROUTES.packages}/${item.id}`}
              bg='transparent'
              icon={
                <ChevronIcon
                  fill={packageItem.receiptIconColor}
                  width={14}
                  height={16}
                />
              }
            />
          </Flex>
        </GridItem>
      </Grid>

      {!isLastItem && (
        <Divider
          position='absolute'
          width={{ base: '100%', lg: 'calc(100% - 40px)' }}
          left={{ lg: '20px' }}
          borderBottomColor={{
            base: packageItem.mobileDividerColor,
            lg: packageItem.dividerColor
          }}
        />
      )}
    </Box>
  );
};

export default PackageItem;
